import React, { useState, useEffect } from 'react';
import { collection, addDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

interface VenueRequestFormProps {
  userId: string;
}

interface VenueRequest {
  venueName: string;
  location: string;
  phone: string;
}

const VenueRequestForm: React.FC<VenueRequestFormProps> = ({ userId }) => {
  const [formData, setFormData] = useState<VenueRequest>({
    venueName: '',
    location: '',
    phone: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [existingRequest, setExistingRequest] = useState<any>(null);

  useEffect(() => {
    checkExistingRequest();
  }, [userId]);

  const checkExistingRequest = async () => {
    try {
      const q = query(
        collection(db, 'venueRequests'),
        where('userId', '==', userId),
        where('status', '==', 'pending')
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        setExistingRequest(snapshot.docs[0].data());
      }
    } catch (err) {
      console.error('Error checking existing request:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await addDoc(collection(db, 'venueRequests'), {
        userId,
        venueName: formData.venueName,
        location: formData.location,
        phone: formData.phone,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      setSubmitted(true);
      setFormData({
        venueName: '',
        location: '',
        phone: ''
      });
    } catch (err) {
      console.error('Error submitting request:', err);
      setError('Hiba történt a kérelem elküldésekor. Kérjük próbálja újra.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (existingRequest) {
    return (
      <div className="card-dark p-8">
        <div className="text-center py-8">
          <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">Kérelem feldolgozás alatt</h3>
          <p className="text-[#B8B8C3] mb-6">
            Van egy folyamatban lévő üzemeltetői kérelme. Az adminisztrátorok hamarosan feldolgozzák.
          </p>
          <div className="bg-[#15191E] rounded-lg p-6 text-left max-w-md mx-auto">
            <div className="space-y-3">
              <div>
                <p className="text-sm text-[#B8B8C3]">Létesítmény neve</p>
                <p className="text-white font-semibold">{existingRequest.venueName}</p>
              </div>
              <div>
                <p className="text-sm text-[#B8B8C3]">Helyszín</p>
                <p className="text-white font-semibold">{existingRequest.location}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="card-dark p-8">
        <div className="text-center py-8">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">Kérelem elküldve!</h3>
          <p className="text-[#B8B8C3] mb-6">
            Köszönjük a jelentkezését! Az adminisztrátorok hamarosan feldolgozzák a kérelmét.
          </p>
          <button
            onClick={() => setSubmitted(false)}
            className="px-6 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors"
          >
            Új kérelem
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card-dark p-8">
      <div className="mb-6">
        <h3 className="text-2xl font-bold text-white mb-2">🏢 Üzemeltetői kérelem</h3>
        <p className="text-[#B8B8C3]">
          Töltse ki az alábbi űrlapot, hogy üzemeltetői jogosultságot kérjen a létesítményéhez.
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-white font-semibold mb-2">
            Létesítmény neve *
          </label>
          <input
            type="text"
            name="venueName"
            value={formData.venueName}
            onChange={handleChange}
            required
            placeholder="pl. Darts Club Budapest"
            className="w-full px-4 py-3 bg-[#15191E] border border-[#31383D] rounded-lg text-white placeholder-[#8a8f99] focus:outline-none focus:border-cyan-500"
          />
        </div>

        <div>
          <label className="block text-white font-semibold mb-2">
            Helyszín (cím) *
          </label>
          <input
            type="text"
            name="location"
            value={formData.location}
            onChange={handleChange}
            required
            placeholder="pl. Budapest, Váci utca 1."
            className="w-full px-4 py-3 bg-[#15191E] border border-[#31383D] rounded-lg text-white placeholder-[#8a8f99] focus:outline-none focus:border-cyan-500"
          />
        </div>

        <div>
          <label className="block text-white font-semibold mb-2">
            Telefonszám *
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            placeholder="pl. +36 30 123 4567"
            className="w-full px-4 py-3 bg-[#15191E] border border-[#31383D] rounded-lg text-white placeholder-[#8a8f99] focus:outline-none focus:border-cyan-500"
          />
        </div>

        <div className="pt-4">
          <button
            type="submit"
            disabled={loading}
            className="w-full px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-lg hover:shadow-[0_0_20px_rgba(6,182,212,0.5)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Küldés...
              </span>
            ) : (
              'Kérelem elküldése'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default VenueRequestForm;
